import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, hasSupabaseConfig } from '../lib/supabaseClient';
import { createApiClient } from '../services/api';

const AuthContext = createContext(null);

const operatorStorageKey = 'recruit_operator_code';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem(operatorStorageKey);
    setUser(null);
  }, []);

  useEffect(() => {
    const operatorCode = localStorage.getItem(operatorStorageKey);
    if (!operatorCode || !hasSupabaseConfig || !supabase) {
      setLoading(false);
      return;
    }

    supabase.rpc('get_recruit_operator', { p_operator_code: operatorCode })
      .then(({ data, error }) => {
        if (error || !data || !data.length) {
          logout();
          setLoading(false);
          return;
        }

        const operator = data[0];
        setUser({
          operatorCode: operator.operator_code,
          name: operator.name,
          companyName: operator.company_name,
          role: operator.role_code,
          roleName: operator.role_name,
          permissions: operator.permissions || {},
        });
        setLoading(false);
      })
      .catch(() => {
        logout();
        setLoading(false);
      });
  }, [logout]);

  const login = async (operatorCode) => {
    if (!hasSupabaseConfig || !supabase) {
      throw new Error('Supabase is not configured');
    }

    const code = String(operatorCode || '').trim();
    const { data, error } = await supabase.rpc('get_recruit_operator', { p_operator_code: code });
    if (error) throw error;
    if (!data || !data.length) throw new Error('Operator code not found');

    const operator = data[0];
    localStorage.setItem(operatorStorageKey, operator.operator_code);
    const nextUser = {
      operatorCode: operator.operator_code,
      name: operator.name,
      companyName: operator.company_name,
      role: operator.role_code,
      roleName: operator.role_name,
      permissions: operator.permissions || {},
    };
    setUser(nextUser);
    return nextUser;
  };

  const register = async () => {
    throw new Error('Register is disabled in operator mode');
  };

  const authAxios = useCallback(() => {
    return createApiClient(user);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, token: null, loading, login, register, logout, authAxios, API: null }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
